import { useCallback, useEffect, useState } from 'react';

import { DBSchema, openDB, type IDBPDatabase } from 'idb';

interface DB extends DBSchema {
  case: {
    key: string;
    value: {
      caseId: string;
      facts: { [key: string]: boolean };
      documents: { [key: string]: boolean };
    };
    indexes: {
      caseId: string;
    };
  };
}

const DATABASE_NAME = 'wexler-db';

let dbPromise: Promise<IDBPDatabase<DB>> | null = null;

const getDb = async (): Promise<IDBPDatabase<DB>> => {
  if (dbPromise) return dbPromise;

  dbPromise = openDB<DB>(DATABASE_NAME, 2, {
    async upgrade(db, oldVersion, _newVersion, transaction) {
      // Create the cases store if it doesn't exist
      if (!db.objectStoreNames.contains('case')) {
        const objectStore = db.createObjectStore('case', { keyPath: 'caseId' });
        objectStore.createIndex('caseId', 'caseId', { unique: true });
      }

      // Migration from v1 to v2: Add documents property to existing case records
      if (oldVersion < 2) {
        const store = transaction.objectStore('case');
        const allCases = await store.getAll();
        for (const caseRecord of allCases) {
          if (!caseRecord.documents) {
            caseRecord.documents = {};
            await store.put(caseRecord);
          }
        }
      }
    },
  });

  return await dbPromise;
};

export function useIndexedDb() {
  const [db, setDb] = useState<IDBPDatabase<DB>>();

  useEffect(() => {
    getDb().then(setDb);
  }, []);

  const doesCaseExist = useCallback(
    async (subsetId: string) => {
      if (!db) return null;
      let caseRecord = await db.get('case', subsetId);
      return caseRecord !== undefined;
    },
    [db],
  );

  // Fact functions
  const selectFact = useCallback(
    async (subsetId: string, factId: string) => {
      if (!db) return null;

      let caseRecord = await db.get('case', subsetId);

      if (!caseRecord) {
        // Create new case record with this fact selected
        caseRecord = {
          caseId: subsetId,
          facts: { [factId]: true },
          documents: {},
        };
      } else {
        caseRecord.facts[factId] = true;
      }

      await db.put('case', caseRecord);
    },
    [db],
  );

  const deselectFact = useCallback(
    async (subsetId: string, factId: string) => {
      if (!db) return;

      const caseRecord = await db.get('case', subsetId);
      if (!caseRecord) return;

      delete caseRecord.facts[factId];

      await db.put('case', caseRecord);
    },
    [db],
  );

  const isFactSelected = useCallback(
    async (subsetId: string, factId: string): Promise<boolean> => {
      if (!db) return false;

      const caseRecord = await db.get('case', subsetId);
      if (!caseRecord) return false;

      return caseRecord.facts[factId] === true;
    },
    [db],
  );

  const deselectAllFacts = useCallback(
    async (subsetId: string) => {
      if (!db) return null;

      const caseRecord = await db.get('case', subsetId);
      if (!caseRecord) return;

      caseRecord.facts = {};

      await db.put('case', caseRecord);
    },
    [db],
  );

  const selectMultipleFacts = useCallback(
    async (subsetId: string, factIds: string[]) => {
      if (!db) return null;

      let caseRecord = await db.get('case', subsetId);

      if (!caseRecord) {
        // Create new case record with these facts selected
        const facts: { [key: string]: boolean } = {};

        factIds.forEach((id) => {
          facts[id] = true;
        });

        caseRecord = {
          caseId: subsetId,
          facts,
          documents: {},
        };
      } else {
        // Add all fact IDs to the existing record
        factIds.forEach((id) => {
          caseRecord!.facts[id] = true;
        });
      }

      await db.put('case', caseRecord);
    },
    [db],
  );

  const deselectMultipleFacts = useCallback(
    async (subsetId: string, factIds: string[]) => {
      if (!db) return null;

      const caseRecord = await db.get('case', subsetId);
      if (!caseRecord) return;

      factIds.forEach((id) => {
        delete caseRecord.facts[id];
      });

      await db.put('case', caseRecord);
    },
    [db],
  );

  const getAllSelectedFactIds = useCallback(
    async (subsetId: string): Promise<string[]> => {
      if (!db) return [];

      const caseRecord = await db.get('case', subsetId);
      if (!caseRecord) return [];

      return Object.keys(caseRecord.facts);
    },
    [db],
  );

  const getAllSelectedFacts = useCallback(
    async (subsetId: string): Promise<{ [key: string]: boolean }> => {
      if (!db) return {};

      const caseRecord = await db.get('case', subsetId);
      if (!caseRecord) return {};

      return caseRecord.facts;
    },
    [db],
  );

  // Document functions
  const selectDocument = useCallback(
    async (subsetId: string, documentId: string) => {
      if (!db) return null;

      let caseRecord = await db.get('case', subsetId);

      if (!caseRecord) {
        // Create new case record with this document selected
        caseRecord = {
          caseId: subsetId,
          facts: {},
          documents: { [documentId]: true },
        };
      } else {
        caseRecord.documents[documentId] = true;
      }

      await db.put('case', caseRecord);
    },
    [db],
  );

  const deselectDocument = useCallback(
    async (subsetId: string, documentId: string) => {
      if (!db) return;

      const caseRecord = await db.get('case', subsetId);
      if (!caseRecord) return;

      delete caseRecord.documents[documentId];

      await db.put('case', caseRecord);
    },
    [db],
  );

  const isDocumentSelected = useCallback(
    async (subsetId: string, documentId: string): Promise<boolean> => {
      if (!db) return false;

      const caseRecord = await db.get('case', subsetId);
      if (!caseRecord) return false;

      return caseRecord.documents[documentId] === true;
    },
    [db],
  );

  const deselectAllDocuments = useCallback(
    async (subsetId: string) => {
      if (!db) return null;

      const caseRecord = await db.get('case', subsetId);
      if (!caseRecord) return;

      caseRecord.documents = {};

      await db.put('case', caseRecord);
    },
    [db],
  );

  const selectMultipleDocuments = useCallback(
    async (subsetId: string, documentIds: string[]) => {
      if (!db) return null;

      let caseRecord = await db.get('case', subsetId);

      if (!caseRecord) {
        // Create new case record with these documents selected
        const documents: { [key: string]: boolean } = {};

        documentIds.forEach((id) => {
          documents[id] = true;
        });

        caseRecord = {
          caseId: subsetId,
          facts: {},
          documents,
        };
      } else {
        // Add all document IDs to the existing record
        documentIds.forEach((id) => {
          caseRecord!.documents[id] = true;
        });
      }

      await db.put('case', caseRecord);
    },
    [db],
  );

  const deselectMultipleDocuments = useCallback(
    async (subsetId: string, documentIds: string[]) => {
      if (!db) return null;

      const caseRecord = await db.get('case', subsetId);
      if (!caseRecord) return;

      documentIds.forEach((id) => {
        delete caseRecord.documents[id];
      });

      await db.put('case', caseRecord);
    },
    [db],
  );

  const getAllSelectedDocumentIds = useCallback(
    async (subsetId: string): Promise<string[]> => {
      if (!db) return [];

      const caseRecord = await db.get('case', subsetId);
      if (!caseRecord) return [];

      return Object.keys(caseRecord.documents);
    },
    [db],
  );

  const getAllSelectedDocuments = useCallback(
    async (subsetId: string): Promise<{ [key: string]: boolean }> => {
      if (!db) return {};

      const caseRecord = await db.get('case', subsetId);
      if (!caseRecord) return {};

      return caseRecord.documents;
    },
    [db],
  );

  return {
    db,
    doesCaseExist,
    // Fact functions
    selectFact,
    deselectFact,
    isFactSelected,
    getAllSelectedFactIds,
    getAllSelectedFacts,
    deselectAllFacts,
    selectMultipleFacts,
    deselectMultipleFacts,
    // Document functions
    selectDocument,
    deselectDocument,
    isDocumentSelected,
    getAllSelectedDocumentIds,
    getAllSelectedDocuments,
    deselectAllDocuments,
    selectMultipleDocuments,
    deselectMultipleDocuments,
  };
}
